import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import ReassuranceBar from '@/components/feature/ReassuranceBar';
import CTABanner from '@/components/feature/CTABanner';
import SEOHead from '@/components/feature/SEOHead';
import { useState } from 'react';

const faqData = [
  {
    q: "Quels types de stores réparez-vous à Lyon ?",
    a: "Nous intervenons sur tous types de stores : stores bannes (toile et mécanisme), brise-soleil orientables (BSO), stores extérieurs déroulants, stores intérieurs. Nous traitons les pannes mécaniques, électriques et de motorisation, toutes marques confondues.",
  },
  {
    q: "Mon store banne est bloqué, puis-je le forcer ?",
    a: "Non, ne forcez jamais un store banne bloqué. Vous risqueriez d'endommager le moteur, les bras ou la toile, ce qui aggraverait la panne et pourrait rendre la réparation plus coûteuse. Contactez-nous pour un diagnostic et un déblocage en toute sécurité.",
  },
  {
    q: "Pouvez-vous remplacer uniquement la toile de mon store ?",
    a: "Oui, nous remplaçons les toiles de store banne à Lyon. Nous prenons les mesures précises, vous conseillons sur le choix du tissu (couleur, matière, résistance UV) et assurons la pose. Le remplacement de toile est une alternative économique au changement complet du store.",
  },
];

const servicesStore = [
  {
    icon: 'ri-home-gear-line',
    title: 'Réparation store banne',
    description: 'Toile déchirée, bras cassé, moteur HS ? Réparation et remplacement de store banne à Lyon.',
    href: '/reparation-store-banne-lyon/',
  },
  {
    icon: 'ri-close-circle-line',
    title: 'Store banne bloqué',
    description: 'Votre store banne ne s\'ouvre plus ou ne se ferme plus ? Déblocage rapide à Lyon, 7j/7.',
    href: '/store-banne-bloque-lyon/',
  },
  {
    icon: 'ri-cpu-line',
    title: 'Motorisation de store',
    description: 'Automatisez votre store manuel ou remplacez un moteur défectueux. Radio, télécommande, capteurs.',
    href: '/motorisation-store-lyon/',
  },
  {
    icon: 'ri-contrast-drop-2-line',
    title: 'Réparation BSO',
    description: 'Brise-soleil orientable : lames, moteur, télécommande. Réparation et réglage à Lyon.',
    href: '/reparation-bso-lyon/',
  },
  {
    icon: 'ri-plug-line',
    title: 'Store électrique',
    description: 'Store extérieur ou déroulant motorisé en panne ? Dépannage et pose à Lyon.',
    href: '/store-electrique-lyon/',
  },
];

export default function StorePilier() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  const serviceJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: 'Réparation de store à Lyon : banne, BSO et motorisation',
    provider: { '@type': 'HomeAndConstructionBusiness', name: 'DFPRO Lyon', telephone: '+33980803684' },
    areaServed: [{ '@type': 'City', name: 'Lyon' }, { '@type': 'AdministrativeArea', name: 'Métropole de Lyon' }],
    description: 'Réparation de store banne, BSO et store électrique à Lyon. Intervention 24-48h, 7j/7. Devis gratuit, garantie 10 ans.',
  };

  const faqJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqData.map((item) => ({ '@type': 'Question', name: item.q, acceptedAnswer: { '@type': 'Answer', text: item.a } })),
  };

  const breadcrumbJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Accueil', item: 'https://depanfenetres-lyon.fr/' },
      { '@type': 'ListItem', position: 2, name: 'Réparation store à Lyon', item: 'https://depanfenetres-lyon.fr/reparation-store-lyon/' },
    ],
  };

  return (
    <>
      <SEOHead
        title="Réparation de store à Lyon | Store banne & BSO | DFPRO"
        description="Store banne bloqué, BSO en panne, motorisation HS ? Réparation de store à Lyon, 24-48h, 7j/7. Devis gratuit, garantie 10 ans. Appelez DFPRO Lyon."
        canonicalUrl="https://depanfenetres-lyon.fr/reparation-store-lyon/"
        ogTitle="Réparation store à Lyon — Banne, BSO, motorisation | DFPRO"
        ogDescription="Store banne bloqué, BSO en panne ? Réparation store Lyon 24-48h, devis gratuit, garantie 10 ans."
        jsonLd={{ ...serviceJsonLd, ...faqJsonLd, ...breadcrumbJsonLd }}
      />

      <Header />

      <main>
        <div className="pt-20 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-3">
            <nav className="flex items-center gap-2 text-xs text-secondary-400">
              <a href="/" className="hover:text-primary-500 transition-colors">Accueil</a>
              <i className="ri-arrow-right-s-line w-3 h-3 flex items-center justify-center"></i>
              <span className="text-foreground-700 font-medium">Réparation de store à Lyon</span>
            </nav>
          </div>
        </div>

        <section className="relative w-full min-h-[420px] md:min-h-[480px] flex items-center bg-background-50">
          <div className="absolute inset-0">
            <img
              src="/images/store-pilier-hero-2026.jpg"
              alt="Réparation de store à Lyon — technicien DFPRO Lyon intervenant sur un store banne"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-foreground-900/50 via-foreground-900/35 to-foreground-900/50"></div>
          </div>
          <div className="relative z-10 w-full px-4 md:px-6 py-14 md:py-16">
            <div className="max-w-3xl mx-auto text-center">
              <h1 className="font-heading font-bold text-2xl md:text-4xl text-background-50 leading-tight">
                Réparation de store à Lyon : banne, BSO et motorisation
              </h1>
              <p className="mt-4 md:mt-5 text-sm md:text-lg text-background-50/90 max-w-2xl mx-auto leading-relaxed">
                Store banne bloqué, BSO en panne ou motorisation défectueuse ? Nos techniciens réparent tous types de stores à Lyon et dans la métropole. Intervention rapide, devis gratuit.
              </p>
              <div className="mt-7 flex flex-col sm:flex-row items-center justify-center gap-3">
                <a href="tel:+33980803684" className="inline-flex items-center gap-2 bg-accent-500 hover:bg-accent-600 text-background-50 font-semibold text-sm md:text-base px-6 py-3.5 rounded-md transition-colors whitespace-nowrap cursor-pointer shadow-[0_2px_8px_rgba(242,140,40,0.4)]">
                  <i className="ri-phone-fill text-lg w-5 h-5 flex items-center justify-center"></i>
                  Appeler le 09 80 80 36 84
                </a>
                <a href="/contact/" className="inline-flex items-center gap-2 border-2 border-background-50/60 hover:border-background-50 text-background-50 font-semibold text-sm md:text-base px-6 py-3.5 rounded-md transition-colors whitespace-nowrap cursor-pointer">
                  <i className="ri-file-list-3-line w-5 h-5 flex items-center justify-center"></i>
                  Devis gratuit
                </a>
              </div>
            </div>
          </div>
        </section>

        <ReassuranceBar />

        <section className="w-full bg-background-50 py-14 md:py-20">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 md:gap-12">
              <div className="lg:col-span-2">
                <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground-800 mb-5">
                  Votre store est en panne à Lyon ? Nous le réparons
                </h2>
                <div className="space-y-4 text-sm md:text-base text-secondary-500 leading-relaxed">
                  <p>
                    Un store en panne, c'est une protection solaire qui ne joue plus son rôle. Que vous ayez un store banne, un brise-soleil orientable (BSO), un store extérieur déroulant ou un store intérieur, <strong className="text-foreground-800">DFPRO Lyon intervient pour diagnostiquer et réparer</strong> rapidement votre équipement. Nous couvrons tout Lyon et la Métropole de Lyon.
                  </p>
                  <p>
                    Les pannes de store sont variées et peuvent concerner aussi bien la <strong className="text-foreground-800">partie mécanique</strong> (bras articulés, toile, coffre, treuil) que la <strong className="text-foreground-800">partie électrique et électronique</strong> (moteur, télécommande, capteur vent/soleil, fin de course). Notre premier travail consiste à identifier précisément l'origine de la panne pour proposer la réparation la plus adaptée.
                  </p>
                  <p>
                    Dans de nombreux cas, la réparation est la solution la plus économique. Un remplacement de moteur, une réparation de bras ou un changement de toile permet de redonner plusieurs années de vie à votre store sans avoir à le changer entièrement. Si le store est trop ancien et que la réparation n'est pas pertinente, nous vous le disons honnêtement et vous conseillons sur le matériel de remplacement.
                  </p>
                  <p>
                    Nous intervenons sur toutes les marques de stores présentes sur le marché lyonnais : <strong className="text-foreground-800">Somfy, Bubendorff, Profalux, Nice, Simu, Franciaflex</strong> et bien d'autres. Nos techniciens connaissent les spécificités de chaque fabricant et disposent des pièces détachées nécessaires pour une réparation efficace.
                  </p>
                </div>
              </div>

              <div className="bg-background-100 rounded-lg p-5 md:p-6 h-fit">
                <h3 className="font-heading font-semibold text-base text-foreground-800 mb-4">Nos services store</h3>
                <nav className="space-y-2">
                  {[
                    { label: 'Réparation store banne', href: '/reparation-store-banne-lyon/' },
                    { label: 'Store banne bloqué', href: '/store-banne-bloque-lyon/' },
                    { label: 'Motorisation de store', href: '/motorisation-store-lyon/' },
                    { label: 'Réparation BSO', href: '/reparation-bso-lyon/' },
                    { label: 'Store électrique', href: '/store-electrique-lyon/' },
                  ].map((link, idx) => (
                    <a key={idx} href={link.href} className="flex items-center gap-2 text-sm text-secondary-500 hover:text-primary-500 transition-colors py-1.5 cursor-pointer">
                      <i className="ri-arrow-right-s-line text-primary-400 w-4 h-4 flex items-center justify-center"></i>
                      {link.label}
                    </a>
                  ))}
                </nav>
                <div className="mt-5 pt-5 border-t border-background-200">
                  <a href="tel:+33980803684" className="inline-flex items-center justify-center gap-2 w-full bg-accent-500 hover:bg-accent-600 text-background-50 font-semibold text-sm py-3 rounded-md transition-colors cursor-pointer">
                    <i className="ri-phone-fill w-4 h-4 flex items-center justify-center"></i>
                    Appeler — 09 80 80 36 84
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="w-full bg-background-100 py-14 md:py-20">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-10 md:mb-14">
              <h2 className="font-heading font-bold text-xl md:text-3xl text-foreground-800">
                Tous nos services de réparation de store à Lyon
              </h2>
              <p className="mt-3 text-sm md:text-base text-secondary-500 max-w-2xl mx-auto">
                Du store banne au BSO, en passant par la motorisation, nous couvrons l'ensemble des besoins de dépannage de stores.
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
              {servicesStore.map((srv, idx) => (
                <a key={idx} href={srv.href} className="group block bg-background-50 rounded-lg border border-background-200 hover:border-primary-200 transition-all duration-200 cursor-pointer p-5 md:p-6">
                  <div className="w-12 h-12 rounded-md bg-primary-50 flex items-center justify-center mb-4">
                    <i className={`${srv.icon} text-primary-400 text-xl w-5 h-5 flex items-center justify-center`}></i>
                  </div>
                  <h3 className="font-heading font-semibold text-base text-foreground-800 mb-2">{srv.title}</h3>
                  <p className="text-sm text-secondary-500 leading-relaxed">{srv.description}</p>
                  <span className="inline-flex items-center gap-1 mt-3 text-sm font-medium text-primary-500 group-hover:text-primary-600 transition-colors">
                    En savoir plus
                    <i className="ri-arrow-right-line text-xs w-3 h-3 flex items-center justify-center"></i>
                  </span>
                </a>
              ))}
            </div>
          </div>
        </section>

        <section className="w-full bg-background-50 py-14 md:py-20">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-10 md:mb-14">
              <h2 className="font-heading font-bold text-xl md:text-3xl text-foreground-800">
                Types de stores que nous réparons
              </h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
              {[
                { title: 'Store banne', desc: 'Le store de terrasse classique, à bras articulés. Réparation toile, bras, moteur, coffre. Toutes dimensions et marques.' },
                { title: 'BSO (brise-soleil orientable)', desc: 'Store à lames orientables en aluminium. Réparation lames, moteur, télécommande, réglage de fin de course.' },
                { title: 'Store extérieur déroulant', desc: 'Store vertical qui descend devant une fenêtre ou baie. Pannes de moteur, toile, axe, guidage latéral.' },
                { title: 'Store intérieur', desc: 'Store vénitien, store enrouleur, store plissé. Réparation mécanisme, remplacement de toile, motorisation.' },
                { title: 'Store solaire / screen', desc: 'Toile screen micro-perforée pour terrasses et vérandas. Réparation et remplacement de toile.' },
                { title: 'Store de véranda', desc: 'Store sous toiture ou store latéral de véranda. Réparation rails, toile et motorisation.' },
              ].map((item, idx) => (
                <div key={idx} className="bg-background-100 rounded-lg p-5 md:p-6">
                  <h3 className="font-heading font-semibold text-base text-foreground-800 mb-2">{item.title}</h3>
                  <p className="text-sm text-secondary-500 leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="w-full bg-background-100 py-14 md:py-20">
          <div className="max-w-3xl mx-auto px-4 md:px-6">
            <div className="text-center mb-10 md:mb-14">
              <h2 className="font-heading font-bold text-xl md:text-3xl text-foreground-800">
                Questions fréquentes — Réparation store à Lyon
              </h2>
            </div>
            <div className="space-y-3">
              {faqData.map((item, idx) => (
                <div key={idx} className="bg-background-50 rounded-lg border border-background-200 overflow-hidden">
                  <button onClick={() => setOpenFaq(openFaq === idx ? null : idx)} className="w-full flex items-center justify-between px-5 py-4 text-left cursor-pointer">
                    <span className="font-heading font-semibold text-sm md:text-base text-foreground-800 pr-4">{item.q}</span>
                    <span className={`flex-shrink-0 transition-transform duration-200 ${openFaq === idx ? 'rotate-180' : ''}`}>
                      <i className="ri-arrow-down-s-line text-secondary-400 w-5 h-5 flex items-center justify-center"></i>
                    </span>
                  </button>
                  {openFaq === idx && (
                    <div className="px-5 pb-4"><p className="text-sm text-secondary-500 leading-relaxed">{item.a}</p></div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        <CTABanner
          title="Store en panne à Lyon ?"
          subtitle="Appelez le 09 80 80 36 84 pour un dépannage rapide. Devis gratuit, garantie 10 ans."
          buttonText="Appeler maintenant"
          secondaryButtonText="Demander un devis"
          secondaryButtonHref="/contact/"
        />
      </main>
      <Footer />
    </>
  );
}